import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiError,
  countOpportunities,
  isAbort,
  listOpportunities,
  MAX_IDS_PER_REQUEST,
  type OpportunityQuery,
} from '../../shared/api';
import type { Opportunity } from '../../shared/opportunity';

/**
 * Explore's list, a page at a time, the way the API serves it.
 *
 * `GET /opportunities` is keyset-paginated in the order the site lists events.
 * Each response carries a `nextCursor` to the page after it and a `prevCursor`
 * to the page before, so a list opened partway down the week — to keep the
 * reader's place across a filter change — fills in upwards as well as down.
 *
 * A cursor belongs to the query that produced it, so the next page is asked for
 * with that query exactly, never rebuilt from the filters. Changing the filters
 * (a new `key`) starts again from a first page, at `anchor` when there is one.
 *
 * While a new first page is on its way the previous results stay on screen, so
 * a filter change never flashes an empty list. A response for a query that is
 * no longer current is dropped, and one page request runs at a time.
 */

/** Matches the list's old render step, so each scroll adds as many cards as it did. */
export const PAGE_LIMIT = 40;

/** Where a freshly loaded first page begins. */
export type Landing = 'top' | 'anchor' | 'end';

interface State {
  /** The filter signature `items` were fetched for. */
  key: string | null;
  /** The request that produced the cursors, reused for every later page. */
  query: OpportunityQuery | null;
  items: Opportunity[];
  next: string | null;
  prev: string | null;
  /** Bumped each time a first page lands. */
  generation: number;
  landing: Landing;
  anchorId: string | null;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  loadingMore: boolean;
  moreError: boolean;
}

export interface FeedInput {
  /** Filter signature. A change starts again from a first page. */
  key: string;
  /** The current filters as a request, or null when they can only match nothing. */
  query: OpportunityQuery | null;
  /** The event being read, asked for when a new first page is requested. */
  anchor: () => string | null;
}

export interface OpportunityFeed {
  /** What the API returned, in order: every page loaded for the current request. */
  items: Opportunity[];
  generation: number;
  landing: Landing;
  /** The anchor the current first page was asked for. */
  anchorId: string | null;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  /** A first page for different filters is on its way; `items` are the previous results. */
  stale: boolean;
  hasMore: boolean;
  hasPrevious: boolean;
  loadingMore: boolean;
  /** The last request for another page failed. Loaded pages are kept. */
  moreError: boolean;
  loadMore: () => void;
  loadPrevious: () => void;
  /** Starts again from a first page, at the current anchor. */
  retry: () => void;
}

const INITIAL: State = {
  key: null,
  query: null,
  items: [],
  next: null,
  prev: null,
  generation: 0,
  landing: 'top',
  anchorId: null,
  status: 'loading',
  error: null,
  loadingMore: false,
  moreError: false,
};

const describe = (err: unknown): string =>
  err instanceof ApiError ? err.message : "We couldn't load the events";

const withoutRepeats = (items: Opportunity[], seen: Opportunity[]) => {
  // Keyset pages cannot overlap, but a retried request can land twice.
  const ids = new Set(seen.map((o) => o.id));
  return items.filter((o) => !ids.has(o.id));
};

export function useOpportunityFeed({ key, query, anchor }: FeedInput): OpportunityFeed {
  const [state, setState] = useState<State>(INITIAL);
  const [attempt, setAttempt] = useState(0);

  const generation = useRef(0);
  const live = useRef(state);
  live.current = state;
  const latest = useRef({ key, query, anchor });
  latest.current = { key, query, anchor };
  /** Set synchronously, so two sentinel callbacks in one frame fetch one page. */
  const busy = useRef(false);
  const pages = useRef(new Set<AbortController>());

  useEffect(() => {
    const gen = ++generation.current;
    busy.current = false;
    for (const controller of pages.current) controller.abort();
    pages.current.clear();

    const request = latest.current.query;
    if (!request) {
      setState({ ...INITIAL, key, status: 'ready', generation: gen });
      return;
    }

    const anchorId = latest.current.anchor();
    const controller = new AbortController();
    setState((prev) => ({ ...prev, status: 'loading', error: null, moreError: false }));

    (async () => {
      let page = await listOpportunities(
        { ...request, limit: PAGE_LIMIT, anchor: anchorId ?? undefined },
        controller.signal,
      );
      let landing: Landing = anchorId ? 'anchor' : 'top';

      // Nothing runs as late as where the reader was: show the last of what does.
      if (anchorId && page.items.length === 0 && page.prevCursor) {
        const last = await listOpportunities(
          { ...request, limit: PAGE_LIMIT, cursor: page.prevCursor },
          controller.signal,
        );
        page = { items: last.items, nextCursor: null, prevCursor: last.prevCursor };
        landing = 'end';
      }

      if (gen !== generation.current) return;
      setState({
        key,
        query: request,
        items: page.items,
        next: page.nextCursor,
        prev: page.prevCursor,
        generation: gen,
        landing,
        anchorId,
        status: 'ready',
        error: null,
        loadingMore: false,
        moreError: false,
      });
    })().catch((err: unknown) => {
      if (controller.signal.aborted || gen !== generation.current) return;
      setState((prev) => ({ ...prev, status: 'error', error: describe(err) }));
    });

    return () => controller.abort();
  }, [key, attempt]);

  const page = useCallback((direction: 'next' | 'prev') => {
    const current = live.current;
    const cursor = direction === 'next' ? current.next : current.prev;
    if (
      busy.current ||
      current.status !== 'ready' ||
      !cursor ||
      !current.query ||
      current.key !== latest.current.key
    ) {
      return;
    }

    const gen = generation.current;
    const controller = new AbortController();
    pages.current.add(controller);
    busy.current = true;
    setState((prev) => ({ ...prev, loadingMore: true, moreError: false }));

    listOpportunities({ ...current.query, limit: PAGE_LIMIT, cursor }, controller.signal)
      .then((result) => {
        if (gen !== generation.current) return;
        setState((prev) =>
          direction === 'next'
            ? {
                ...prev,
                items: [...prev.items, ...withoutRepeats(result.items, prev.items)],
                next: result.nextCursor,
                loadingMore: false,
              }
            : {
                ...prev,
                items: [...withoutRepeats(result.items, prev.items), ...prev.items],
                prev: result.prevCursor,
                loadingMore: false,
              },
        );
      })
      .catch((err: unknown) => {
        if (isAbort(err) || gen !== generation.current) return;
        setState((prev) => ({ ...prev, loadingMore: false, moreError: true }));
      })
      .finally(() => {
        pages.current.delete(controller);
        if (gen === generation.current) busy.current = false;
      });
  }, []);

  const loadMore = useCallback(() => page('next'), [page]);
  const loadPrevious = useCallback(() => page('prev'), [page]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    items: state.items,
    generation: state.generation,
    landing: state.landing,
    anchorId: state.anchorId,
    status: state.status,
    error: state.error,
    stale: state.key !== key,
    hasMore: Boolean(state.next),
    hasPrevious: Boolean(state.prev),
    loadingMore: state.loadingMore,
    moreError: state.moreError,
    loadMore,
    loadPrevious,
    retry,
  };
}

/** Sums a count whose `id` list is longer than one request can carry. */
async function countInChunks(query: OpportunityQuery): Promise<number> {
  const ids = query.id;
  if (!Array.isArray(ids) || ids.length <= MAX_IDS_PER_REQUEST) return countOpportunities(query);
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) chunks.push(ids.slice(i, i + MAX_IDS_PER_REQUEST));
  const totals = await Promise.all(chunks.map((chunk) => countOpportunities({ ...query, id: chunk })));
  return totals.reduce((sum, n) => sum + n, 0);
}

/**
 * Counts in flight, by query. With no filters on, the list, the pool and the
 * toggle beside "Show past events" all ask the same question at once, and one
 * request answers all three.
 */
const inflight = new Map<string, Promise<number>>();

function sharedCount(query: OpportunityQuery): Promise<number> {
  const key = JSON.stringify(query);
  let pending = inflight.get(key);
  if (!pending) {
    pending = countInChunks(query).finally(() => inflight.delete(key));
    inflight.set(key, pending);
  }
  return pending;
}

/**
 * `GET /opportunities/count` for a query, kept current as the query changes.
 *
 * The last answer stays until the next arrives, so a number beside a toggle
 * never blinks to nothing. A null query can only match nothing, and counts 0.
 */
export function useOpportunityCount(
  query: OpportunityQuery | null | undefined,
  /** Changes when the same query should be asked again: the clock moving past an event's end. */
  refresh = '',
): number | null {
  const signature = query === undefined ? undefined : `${JSON.stringify(query)}|${refresh}`;
  const [result, setResult] = useState<{ signature: string | undefined; total: number | null }>({
    signature: undefined,
    total: null,
  });
  const latest = useRef(query);
  latest.current = query;

  useEffect(() => {
    const request = latest.current;
    if (request === undefined) return;
    if (request === null) {
      setResult({ signature, total: 0 });
      return;
    }
    // Not aborted: another count may be sharing the request. A late answer is dropped instead.
    let live = true;
    sharedCount(request)
      .then((total) => {
        if (live) setResult({ signature, total });
      })
      .catch(() => {
        // Keep the last number: a count is a hint, and a failed one is not zero.
      });
    return () => {
      live = false;
    };
  }, [signature]);

  return result.total;
}
